import { Box, Flex, Text } from '@chakra-ui/react';
import { useAppDispatch, useAppSelector } from 'app/store';
import {
  getGlobalSearchByCollaborator,
  getGlobalSearchResults,
  getSearchValue,
} from 'app/store/GlobalSearch/selectors';
import { useCallback, useDeferredValue, useEffect, useState } from 'react';
import { fetchGlobalSearchEffect } from 'app/store/GlobalSearch/effects';
import { debounce } from 'lodash';
import { getPlayerHeight } from 'app/store/AudioPlayer/selectors';
import { useClickedOutsideElement } from 'app/utils/hooks';
import { clearResultsAction } from 'app/store/GlobalSearch/slice';
import { OUTSIDE_SCROLL_ELEMENT_ID } from 'app/constants';
import { SEARCH_DEBOUNCE_MS } from '../Song/SearchBar/constants';
import { HEADER_HEIGHT } from '../HeaderBar';
import { GlobalSearchContent } from './GlobalSearchContent';
import { GLOBAL_SEARCH_INPUT_ID } from './GlobalSearchInput';
import { useShowGlobalSearch } from './hooks';

export const GlobalSearch = () => {
  const search = useAppSelector(getSearchValue);
  const [tabIndex, setTabIndex] = useState(0);
  const results = useAppSelector(getGlobalSearchResults);
  const { collaborators, songs } = useDeferredValue(results);

  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const searchByCollaborator = useAppSelector(getGlobalSearchByCollaborator);

  const isVisible = useShowGlobalSearch();
  const isEmpty = !songs.length && !collaborators.length;

  useEffect(() => {
    if (!isVisible) {
      setTabIndex(0);
    }
  }, [isVisible]);

  const handleTabsChange = (index: number) => {
    setTabIndex(index);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce(async () => {
      await dispatch(fetchGlobalSearchEffect({ newSearch: true, group: true }));
      setLoading(false);
    }, SEARCH_DEBOUNCE_MS),
    [dispatch]
  );

  useEffect(() => {
    setLoading(true);
    debouncedSearch();
  }, [debouncedSearch, search]);

  const playerHeight = useAppSelector(getPlayerHeight);

  const { ref } = useClickedOutsideElement<HTMLDivElement>((e) => {
    const clickedOnPlayer = document.body.clientHeight - e.y < playerHeight;
    const globalSeachInput = document.getElementById(GLOBAL_SEARCH_INPUT_ID);
    const isOutsideContainer = document
      .getElementById(OUTSIDE_SCROLL_ELEMENT_ID)
      ?.contains(e.target as Node);

    if (
      !clickedOnPlayer &&
      !isOutsideContainer &&
      !globalSeachInput?.contains(e.target as Node)
    ) {
      dispatch(clearResultsAction());
    }
  });

  if (!isVisible) {
    return null;
  }

  return (
    <Box
      ref={ref}
      sx={{
        position: 'absolute',
        overflow: 'auto',
        width: '100%',
        height: `calc(100vh - ${HEADER_HEIGHT}px)`,
        zIndex: 1300,
        bg: 'M.10',
        color: 'M.70',
        px: 4,
      }}
    >
      <Flex direction="column" h="full">
        <Box py={6}>
          <Text color="M.70" fontSize="lg" fontWeight={500}>
            Search
          </Text>
          {searchByCollaborator && (
            <Text
              fontSize="sm"
              fontWeight={500}
              color="textSubdued"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              data-testid="collaborator-search-results-title"
            >
              Song Collaborations with {searchByCollaborator.displayName}
            </Text>
          )}
        </Box>
        <GlobalSearchContent
          loading={loading}
          isEmpty={isEmpty}
          collaborators={collaborators}
          songs={songs}
          searchByCollaborator={searchByCollaborator}
          tabIndex={tabIndex}
          handleTabsChange={handleTabsChange}
          search={search}
        />
      </Flex>
    </Box>
  );
};
